import { logNetworkRequest } from 'Diagnostics/diagnostics';
import { CustomErrorType } from 'Models/index';
import fitmentSearchConfig from 'Models/uiConfig/fitmentSearchConfig';
import requestConfig from 'Models/uiConfig/requestConfig';
import type { RequestExtra } from 'Models/uiConfig/requestTypes';
import { convertibleFromObject } from 'Modules/converter/index';
import { isArrayOf, isObjectRecord, isString } from 'Utils/guards';

import { facetsToConvertible, sortToConvertible } from './common/convert';
import { asCmError, GET, GETBACKUP, getErrorSearchResponse } from './common/httpClient';
import ResponseValidationError from './common/responseValidationError';
import { isGenericItem } from './common/validators';
import { VERIFY_FITMENT_ENDPOINT } from './endpoints';
import facets from './facets';
import search from './search';
import {
  type RequestSelectionItem,
  SEARCH_RESPONSE_ERROR_STATES,
  type ServerModel,
  type VerifyFitmentVerdict,
  type VerifyFitsValue,
} from './types';

interface VerifyFitmentRequest {
  productId: string | number;
  variantId?: string | number;
  selection?: RequestSelectionItem[];
}

const NOTES_FIELD = 'verify_fitment_info';
const NOT_FOUND = 'notFound';
const ANY = 'Any';

const { requestDefaults } = requestConfig;

async function verifyFitment(
  { productId, variantId, selection = [] }: VerifyFitmentRequest,
  abortSignal?: AbortSignal,
): Promise<VerifyFitmentVerdict> {
  const defaultFacets = facetsToConvertible({ selection: [] }, requestDefaults);
  const params: RequestExtra = {
    productId: encodeURIComponent(String(productId)),
    ...(variantId ? { variantId: encodeURIComponent(String(variantId)) } : {}),
    ...(requestDefaults.catalog ? { catalog: encodeURIComponent(requestDefaults.catalog) } : {}),
    vehicle: Object.fromEntries(
      selection.flatMap(({ field, term }) =>
        term ? [[encodeURIComponent(field), encodeURIComponent(term)]] : [],
      ),
    ),
    ...(defaultFacets ? { facet: defaultFacets } : {}),
    ...(requestDefaults.sort ? { sort: sortToConvertible(requestDefaults.sort) } : {}),
    ...(requestDefaults.referrer ? { referrer: requestDefaults.referrer } : {}),
    extra: await convertibleFromObject(requestDefaults.extra ?? {}),
  };

  try {
    return getVerdict(await GET(VERIFY_FITMENT_ENDPOINT, params, { signal: abortSignal }));
  } catch (primaryFailure) {
    const primaryError = asCmError(primaryFailure);

    if (primaryError.type === CustomErrorType.requestAborted) {
      return getFailure(primaryFailure);
    }

    console.warn(`The verify fitment request failed: ${primaryError.message}`);

    if (primaryError.status === 429) {
      return getFailure(primaryFailure);
    }

    try {
      const backupVerdict = getVerdict(
        await GETBACKUP(VERIFY_FITMENT_ENDPOINT, params, { signal: abortSignal }),
      );
      logNetworkRequest({ endpoint: VERIFY_FITMENT_ENDPOINT, scenario: 'backup_used', primaryError });
      return backupVerdict;
    } catch (backupFailure) {
      const backupError = asCmError(backupFailure);

      logNetworkRequest({
        endpoint: VERIFY_FITMENT_ENDPOINT,
        scenario: 'backup_failed',
        primaryError,
        backupError,
      });
      return getFailure(backupFailure);
    }
  }
}

export async function verifyFitmentWidget(
  { productId, variantId, selection = [] }: VerifyFitmentRequest,
  abortSignal: AbortSignal,
): Promise<ServerModel.SearchResponse> {
  const [verdict, dropdowns] = await Promise.all([
    verifyFitment({ productId, variantId, selection }, abortSignal),
    search(
      {
        selection,
        pageSize: 0,
        mode: 'YMM',
        extra: fitmentSearchConfig.verifyFitmentDropdownsWithProductId
          ? { productId: String(productId) }
          : {},
      },
      abortSignal,
    ),
  ]);

  if (verdict.state === 'failed') {
    return verdict.response;
  }

  if (abortSignal.aborted) {
    return { ...dropdowns, State: 'cancelled', Items: [], Facets: [] };
  }

  if (SEARCH_RESPONSE_ERROR_STATES.some((state) => state === dropdowns.State)) {
    return dropdowns;
  }

  const hiddenFacets = await getHiddenFacets(selection, verdict.required, dropdowns.Facets, abortSignal);
  if (abortSignal.aborted) {
    return { ...dropdowns, State: 'cancelled', Items: [], Facets: [] };
  }

  const verified = getProduct(productId, verdict);

  return {
    ...dropdowns,
    Items: verified ? [verified] : [],
    Facets: getWidgetFacets(verdict.required, [...dropdowns.Facets, ...hiddenFacets]),
    Actions: verdict.redirect ? [{ Type: 'Redirect', RedirectUrl: verdict.redirect }] : [],
    TotalHits: verified ? 1 : 0,
  };
}

export async function verifyFitmentProduct(
  request: VerifyFitmentRequest,
): Promise<ServerModel.SearchItem | null> {
  return getProduct(request.productId, await verifyFitment(request));
}

// a store plugin may hide a fitment field from the header widget's cascade that this widget shows;
// like the cascade, a base field gets values only while every field before it is selected
async function getHiddenFacets(
  selection: RequestSelectionItem[],
  required: string[] | undefined,
  cascade: ServerModel.FacetCategory[],
  abortSignal: AbortSignal,
): Promise<ServerModel.FacetCategory[]> {
  if (!required) {
    return [];
  }

  const hidden: string[] = [];
  let reachable = true;
  for (const field of required) {
    const inCascade = cascade.find((facet) => facet.FieldName === field);
    if (!inCascade && (reachable || !fitmentSearchConfig.baseFields.includes(field))) {
      hidden.push(field);
    }
    reachable &&= inCascade
      ? !!inCascade.Selection?.length
      : selection.some((item) => item.field === field && !!item.term);
  }
  if (!hidden.length) {
    return [];
  }

  const response = await facets(
    { selection: [...selection.filter((item) => item.term), ...hidden.map((field) => ({ field }))] },
    abortSignal,
  );
  return (response.Facets ?? []).filter((facet) => hidden.includes(facet.FieldName));
}

function getWidgetFacets(
  required: string[] | undefined,
  facetList: ServerModel.FacetCategory[],
): ServerModel.FacetCategory[] {
  const shown = required
    ? facetList
        .filter((facet) => required.includes(facet.FieldName))
        .sort((a, b) => required.indexOf(a.FieldName) - required.indexOf(b.FieldName))
    : facetList;
  return shown.map((facet) => ({
    ...facet,
    ...(facet.Values ? { Values: facet.Values.filter((value) => value.Term !== ANY) } : {}),
    ...(facet.Selection ? { Selection: facet.Selection.filter((value) => value.Term !== ANY) } : {}),
  }));
}

function getProduct(
  productId: string | number,
  verdict: VerifyFitmentVerdict,
): ServerModel.SearchItem | null {
  if (verdict.state !== 'ok') {
    return null;
  }

  const isOpen = verdict.fits === 'specify' || (verdict.fits === 'no' && !!verdict.redirect);

  return {
    id: String(productId),
    ...verdict.product,
    ...verdict.extensions,
    ...(isOpen ? {} : { fitsTheVehicle: verdict.fits as VerifyFitsValue }),
    ...(verdict.notes ? { [NOTES_FIELD]: verdict.notes } : {}),
  };
}

async function getFailure(error: unknown): Promise<VerifyFitmentVerdict> {
  return { state: 'failed', response: await getErrorSearchResponse(error) };
}

function getVerdict(value: unknown): VerifyFitmentVerdict {
  if (
    !isObjectRecord(value) ||
    !isString(value.Fits) ||
    !(value.Redirect === undefined || isString(value.Redirect)) ||
    !(value.Required === undefined || isArrayOf(value.Required, isString)) ||
    !(value.Notes === undefined || isNotes(value.Notes)) ||
    !(value.Product === undefined || isGenericItem(value.Product))
  ) {
    throw new ResponseValidationError(`${VERIFY_FITMENT_ENDPOINT} response validation failed`);
  }

  const { Fits, Redirect, Required, Notes, Product, ...extensions } = value;

  if (Fits === NOT_FOUND) {
    return { state: 'notFound' };
  }

  return {
    state: 'ok',
    fits: Fits,
    redirect: Redirect,
    required: Required,
    notes: Notes,
    product: Product,
    extensions,
  };
}

function isNotes(value: unknown): value is Record<string, string[]> {
  return isObjectRecord(value) && Object.values(value).every((entry) => isArrayOf(entry, isString));
}
